import { DateTime, NaiveDate, Utc } from "@tento-chrono";

import { CalendarSubcolumn } from "./calendar-subcolumn.ts";
import type { CalendarMonthSubcolumn } from "./calendar-month-subcolumn.ts";
import type { CanvasRendererProvider } from "./canvas-renderer-provider.ts";
import type { ChartRendererProvider } from "./chart-renderer-provider.ts";
import type { TimelinePlugin } from "@tento-lona/sheets-ui/plugins/layout/timeline-shared/timeline-content-provider.ts";
import type { ScrollEngine } from "@tento-scroll/engine/scroll-engine";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import {
  Actor,
  type AliasInstance,
  type Option,
  type Role,
  type RowEnvelopes,
  Timescale,
} from "@tento-lona/sheets";
import type { LonaSdk, RowTypeDescriptor, RowTypePreview } from "@tento-lona";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { SheetViewModel, type SheetRow } from "@tento-lona";
import type { JsonObject, RowLocalId, LayoutSpec } from "@tento-lona/sheets";
import { FixedChildSpec } from "@tento-ui/components/sheet/fixed-child-culling";
import { SheetDelegate } from "./sheet-delegate.ts";
import {
  type RegistrationLookup,
  RegistrationReceipt,
} from "./registration-receipt.ts";
import type { TextMeasurer } from "./default-legend-layout.ts";
import type { HomeCardFactory } from "../home-card/home-card-factory.ts";
import type { CellLayoutOptions } from "@tento-ui/components/sheet/cell-layout";
import {
  RenderBinding,
  RenderPluginSpec,
  type RenderPluginLookup,
} from "@tento-lona/sheets/platform";
import { liveVariantOf } from "./live-variant.ts";
import type { SheetBinder } from "./sheet-index-renderer.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

// ── ContentProviders ─────────────────────────────────────────────

/** Typed record of layout providers a child plugin can participate in.
 *  Each layout picks its own key. Omit layouts you don't support. */
export type ContentProviders = {
  calendar?: CalendarSubcolumn.Provider;
  calendarMonth?: CalendarMonthSubcolumn.Provider;
  timeline?: TimelinePlugin.SubcolumnProvider;
  canvas?: CanvasRendererProvider;
  chart?: ChartRendererProvider;
};

// ── Types ────────────────────────────────────────────────────────

/** A fixed element a plugin places in a row's cell area, with its id and alignment. */
export type StaticChildSpec = {
  id: string;
  $element: HTMLElement;
  align?: "center";
  fillWidth?: boolean;
};

/**
 * `V` is the type of `row` the plugin's `bindCell` receives. Defaults
 * to `SheetRow.Joined.Handle` — the lona-js wrapper. Plugins that
 * declare `rendererClass` get their typed rows-side renderer
 * subclass instead (e.g. `CalendarRenderer`) — the infra resolves
 * + asserts the pairing at dispatch time.
 *
 * `sheetRow` is the lona-js `Joined.Handle` that backs the resolved
 * binding row, regardless of V. For alias/layout rows that delegate to
 * a source child renderer, this is the source child so cell reads fetch
 * from the row being rendered.
 */
export type BindCellContext<V = SheetRow.Joined.Handle> = {
  sheet: SheetViewModel.Surface<SheetBinder>;
  row: V;
  sheetRow: SheetRow.Joined.Handle;
  column: ColumnIndex.Key.Resolved.Default;
  widthPx: number;
  inThePast: boolean;
  isCurrent: boolean;
  handle: SheetDelegate;
  /** Current date in the column's timezone. Pre-computed per column to
   *  avoid repeated `tz.toTz(currentTime)` conversions per cell. */
  now: NaiveDate;
};

/** What a plugin's `fixedChildrenForRow` reads: the rendered columns, the sheet surface and its column mapper. */
export interface FixedChildRowContext {
  renderedColumns: ColumnIndex.Key.Resolved.Default.Range;
  sheet: SheetViewModel.Surface<SheetBinder>;
  mapper: ColumnIndex.Mapper;
  now: DateTime<Utc>;
  resolvedWidthPx: number;
  visibleWidthPx: number;
  onFocusCell: Option<(cell: SheetCell.CellId) => void>;
  onBlurCell: Option<(cell: SheetCell.CellId) => void>;
  handle: SheetDelegate;
  /** The band row these fixed children register/render under — the target
   *  row id for any `handle.fixedChild` writes a plugin makes directly
   *  (e.g. a drag ghost on its own owner). May differ from the
   *  param/render row passed to `fixedChildrenForRow`. */
  bandRowId: RowLocalId;
}

/**
 * The element a row's options menu is built in, which can bind to the default
 * or the emoji target.
 */
export interface RowOptionsElement extends HTMLElement {
  bindTarget(target: "default" | "emoji"): boolean;
}

/** Types that go with `RowOptionsElement`: `Factory`, `ComposedRenderer`. */
export namespace RowOptionsElement {
  export type Factory = (props: Factory.Props) => Promise<Option<RowOptionsElement>>;

  /**
   * App-injected renderer for the COMPOSED options surface. The reveal path
   * resolves a visible row's OPTION envelopes (`RowEnvelopes.optionsFor`) and,
   * when non-empty, hands them here INSTEAD of the row's plugin `makeRowOptions`
   * factory — so any row that declares `optionContributions()` gets one composed
   * surface with no per-plugin factory. Lives in the app layer (it maps each
   * `Envelope.input.kind` → widget and drives writes via `sdkRow.envelopes.apply` + the
   * re-render hint); injected once via `Sheet.setComposedOptionsRenderer`.
   * Absent ⇒ the reveal path falls back to the legacy per-plugin factory.
   */
  export type ComposedRenderer = (
    props: Factory.Props & {
      readonly envelopes: readonly RowEnvelopes.Envelope[];
    },
  ) => Promise<Option<RowOptionsElement>>;

  export namespace Factory {
    export type Props = {
      handle: Option<SheetDelegate>;
      row: SheetRow.Joined.Handle;
      dismiss: () => void;
      target: "default" | "emoji";
      standalone: boolean;
      sdk?: LonaSdk.Client;
      /**
       * Alias-instance state when `row` is alias-role; `null`
       * otherwise. Lets plugins key their menu state off the
       * specific instance's props/overrides instead of the
       * template default. Populated by the call site from
       * `row.variant instanceof AliasRow ? row.variant.instance() : null`.
       */
      aliasInstance?: AliasInstance;
    };

    export const NOOP: RowOptionsElement.Factory = async (): Promise<Option<RowOptionsElement>> => null;
  }
}

/** What a plugin's row-options panel explains about the row: the message and an optional learn-more action. */
export type OptionsInfo = {
  message: string | HTMLElement;
  onLearnMore?: (row: SheetRow.Joined.Handle) => void;
};

/** A plugin's legend element class: how the sheet makes a legend and binds it to a row. */
export interface SheetLegendClass<$Legend extends HTMLElement> {
  make(): $Legend;
  bind(
    $legend: $Legend,
    row: SheetRow.Joined.Handle,
    handle: SheetDelegate,
    sheetTimescale: Timescale,
  ): Promise<void> | void;
  /** Compute the natural width of this legend for a given row.
   *  Constant-time, no DOM access. Used by PretextLegendMeasurer. */
  computeWidth(
    row: SheetRow.Joined.Handle,
    measure: TextMeasurer,
    sheetTimescale: string,
  ): number;
}

/**
 * Types and values that go with `RenderPlugin`: `Info`, `Resolved`, `Preview`,
 * `CellActors`, `Base`, `Renderer`, and 4 more.
 */
export namespace RenderPlugin {
  /**
   * What `RenderPluginManager.plugins()` lists for each registered plugin: its
   * id, role and aliases (`null`: none). A listing, not the plugin — look one
   * up with `manager.get(id, role)`.
   */
  export type Info = {
    readonly id: string;
    readonly role: Role;
    readonly aliases: Option<readonly string[]>;
  };

  /**
   * A registered plugin as a lookup hands it out (`manager.get(type, role)`,
   * `manager.get(row).plugin()`, `Resolution.plugin`): every member except
   * the four typed by the plugin's row view — `bindCell`, `onResize`,
   * `rowClass` and `rendererClass`. The manager holds plugins of every row
   * view side by side and never checked which one a lookup found, so it
   * does not claim one; the sheet binder, which resolves each plugin's row
   * itself, is the only caller of those four.
   */
  export type Resolved = Omit<
    RenderPlugin<HTMLElement, HTMLElement>,
    "bindCell" | "onResize" | "rowClass" | "rendererClass"
  >;

  /**
   * A plugin taken at its own row view and held erased: the manager's
   * fallback (`new RenderPluginManager(fallback)`), whose rows need not be
   * `SheetRow.Joined.Handle`.
   */
  export type AtOwnRowView = RenderPlugin<HTMLElement, HTMLElement, unknown>;

  /** Preview data for "create new row" UI: the row type's `RowTypePreview` plus the plugin icon. */
  export interface Preview extends RowTypePreview {
    icon: HTMLTemplateElement;
    /**
     * True when this preview creates an alias / integration row (e.g.
     * Calendar List, Weather) rather than a plain cell or layout row.
     * The picker tints alias icons to set them apart, mirroring how the
     * property drawer tints `instanceof AliasRow` rows. Set by the app
     * layer, which owns the renderer-id → row-type mapping.
     */
    isAlias: boolean;
  }

  /**
   * Per-(rowId, column) `Actor` minter for cell self-echo suppression.
   *
   * Each editable plugin instantiates one (`new RenderPlugin.CellActors(
   * "column.text")`) and uses it inside `bindCell` to mint a stable
   * actor per logical cell position. The plugin passes the actor on
   * writes (`row.writeText(date, val, cellActor)`) and compares it
   * against `change.actor` inside `h.onData` to noop self-echoes — the
   * controlled vs uncontrolled opt-in lives entirely in the plugin's
   * callback.
   *
   * Identity is Symbol-backed (see `Actor` in `@tento-core/changeset-
   * key`); this class caches and returns the same instance per
   * `(rowId, column)` so `===` works across writes and reads.
   *
   * DOM recycling has no effect — the key is the logical position, not
   * the pooled `$cell` element. Entries are bounded by per-session
   * user-interaction count; cleanup on row removal is a follow-up.
   */
  export class CellActors {
    readonly #map = new Map<string, Actor>();
    readonly #namespace: string;
    constructor(namespace: string) {
      this.#namespace = namespace;
    }
    get(
      rowId: RowLocalId,
      column: ColumnIndex.Key.Resolved.Default,
    ): Actor {
      const key = `${rowId}:${column.toString()}`;
      let a = this.#map.get(key);
      if (!a) {
        a = new Actor(`${this.#namespace}:${key}`);
        this.#map.set(key, a);
      }
      return a;
    }
  }

  export interface Base<
    $Cell extends HTMLElement = HTMLElement,
    $Legend extends HTMLElement = HTMLElement,
    V = SheetRow.Joined.Handle,
  > {
    /** Row type this plugin renders. */
    readonly id: string;
    /** Template icon for preview/selection UI. */
    readonly icon: HTMLTemplateElement;
    /** Optional row-sensitive icon override for row chrome surfaces. */
    readonly iconForRow?: (
      row: SheetRow.Joined.Handle,
    ) => HTMLTemplateElement;
    /** Role-qualified plugin identity. */
    readonly role: Role;
    /** Explicit compatibility aliases this plugin should answer. */
    readonly aliases?: readonly string[];
    /**
     * Long-term typed renderer binding. Prefer `rowClass`; older
     * plugins may still use `rendererClass` during the migration.
     */
    readonly rowClass?: new (...args: never[]) => V;
    readonly rendererClass?: new (...args: never[]) => V;
  }

  export interface Renderer<
    $Cell extends HTMLElement = HTMLElement,
    $Legend extends HTMLElement = HTMLElement,
    V = SheetRow.Joined.Handle,
  > extends Base<$Cell, $Legend, V> {
    readonly role: "renderer";
    readonly rowClass?: new (...args: never[]) => V;
  }

  export interface Layout<
    $Cell extends HTMLElement = HTMLElement,
    $Legend extends HTMLElement = HTMLElement,
  > extends Base<$Cell, $Legend, SheetRow.Joined.Handle> {
    readonly role: "layout";
  }

  /**
   * Inert plugin — display-only structural rows (block, group,
   * sheet-footer, header, label, timeline-legend, surface). Same
   * cell/legend shape as Layout; role narrowed to `"inert"` so the
   * descriptor role and plugin role agree.
   */
  export interface Inert<
    $Cell extends HTMLElement = HTMLElement,
    $Legend extends HTMLElement = HTMLElement,
  > extends Base<$Cell, $Legend, SheetRow.Joined.Handle> {
    readonly role: "inert";
  }

  export interface Alias<
    $Cell extends HTMLElement = HTMLElement,
    $Legend extends HTMLElement = HTMLElement,
  > extends Base<$Cell, $Legend, SheetRow.Joined.Handle> {
    readonly role: "alias";
  }

  export interface SourceRow<
    $Cell extends HTMLElement = HTMLElement,
    $Legend extends HTMLElement = HTMLElement,
  > extends Base<$Cell, $Legend, SheetRow.Joined.Handle> {
    readonly role: "source";
  }
}

// ── RenderPlugin ─────────────────────────────────────────────────

/**
 * Render-only plugin interface. Owns DOM creation, data binding, legends,
 * row options UI, and viewport lifecycle. All metadata (layoutSpec,
 * rendersChildren, buildLabel, etc.) lives on the `RowTypeDescriptor` in @tento/lona.
 *
 * Plugins do NOT hold a `RowTypeDescriptor` reference. If they need metadata,
 * they call row.layoutSpec(ts) like any other consumer.
 */
export interface RenderPlugin<
  $Cell extends HTMLElement = HTMLElement,
  $Legend extends HTMLElement = HTMLElement,
  V = SheetRow.Joined.Handle,
> extends RenderPlugin.Base<$Cell, $Legend, V> {

  /**
   * Optional rows-side renderer class. During the migration prefer
   * `rowClass`; the binder still supports legacy `rendererClass`.
   * When either is set, the infra resolves
   * `source.defaultRenderer(ctor)` for each row and passes it as
   * `row` in `BindCellContext`. Plugins without either keep the
   * default V = `SheetRow.Joined.Handle`.
   */
  // ── DOM creation & lifecycle ──────────────────────────────────

  makeCell(): $Cell;
  bindCell($cell: $Cell, context: BindCellContext<V>): void;
  onCellRecycled?($cell: $Cell): void;
  onResize($cell: $Cell, context: BindCellContext<V>): void;

  // ── Data loading ──────────────────────────────────────────────

  loadData: Option<
    (
      sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
      column: NaiveDate.Partial,
    ) => Promise<unknown>
  >;

  // ── Legends ───────────────────────────────────────────────────

  legendFactory: Option<SheetLegendClass<$Legend>>;

  // ── Row options UI ────────────────────────────────────────────

  optionsInfo(row: SheetRow.Joined.Handle): Option<string | OptionsInfo>;
  makeRowOptions: Option<RowOptionsElement.Factory>;

  // ── Viewport lifecycle ────────────────────────────────────────

  onBindRow(sheet: SheetDelegate, row: SheetRow.Joined.Handle): void;
  onViewportColumnsChanged(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
    $legend: Option<$Legend>,
    viewportChangeSource: ScrollEngine.ViewportChangeSource,
  ): void;
  onRenderedColumnsWillChange(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
  ): void;

  // ── Static children (footer overlays, banners) ────────────────

  /** Declarative static children for this row. Called on every sheet bind.
   *  The infrastructure reconciles: adds new, removes stale. */
  staticChildren?(
    row: SheetRow.Joined.Handle,
    handle: SheetDelegate,
  ): StaticChildSpec[];

  // ── Fixed children (spans, overlays) ──────────────────────────

  fixedChildrenForRow?(
    row: SheetRow.Joined.Handle,
    context: FixedChildRowContext,
  ): Promise<FixedChildSpec[]>;

  // ── Layout content providers ──────────────────────────────────

  contentProviders?: ContentProviders;

  // ── Home card ─────────────────────────────────────────────────

  homeCardFactory?: HomeCardFactory;

  // ── RowTypeDescriptor override (feeds back into @tento/lona) ─────────────
  //
  // Allows render-layer plugins to override `RowTypeDescriptor` behavior
  // (e.g. CalendarAllday dynamic heights). Registered on the SDK
  // client at init time, NOT through the plugin manager.

  descriptorOverride?: RowTypeDescriptor.Override;
}

export type AliasChromeDelegate<$Legend extends HTMLElement = HTMLElement> = Pick<
  RenderPlugin<HTMLElement, $Legend>,
  | "icon"
  | "makeRowOptions"
  | "legendFactory"
  | "optionsInfo"
  | "onBindRow"
  | "onViewportColumnsChanged"
  | "onRenderedColumnsWillChange"
  | "staticChildren"
  | "fixedChildrenForRow"
  | "contentProviders"
  | "homeCardFactory"
  | "descriptorOverride"
>;

/**
 * Alias-role plugin that delegates chrome/capabilities to an existing
 * renderer implementation while keeping alias identity role-qualified.
 */
export class AliasChromePlugin<$Legend extends HTMLElement = HTMLElement>
  implements RenderPlugin.Alias<HTMLElement, $Legend>
{
  readonly role = "alias";
  readonly icon: HTMLTemplateElement;
  readonly makeRowOptions: Option<RowOptionsElement.Factory>;
  readonly legendFactory: Option<SheetLegendClass<$Legend>>;
  readonly contentProviders?: ContentProviders;
  readonly homeCardFactory?: HomeCardFactory;
  readonly descriptorOverride?: RowTypeDescriptor.Override;
  readonly loadData = null;

  readonly #delegate: AliasChromeDelegate<$Legend>;
  constructor(
    readonly id: string,
    delegate: AliasChromeDelegate<$Legend>,
    icon: Option<HTMLTemplateElement>,
  ) {
    this.#delegate = delegate;
    this.icon = icon ?? delegate.icon;
    this.makeRowOptions = delegate.makeRowOptions;
    this.legendFactory = delegate.legendFactory;
    if (delegate.contentProviders !== undefined) {
      this.contentProviders = delegate.contentProviders;
    }
    if (delegate.homeCardFactory !== undefined) {
      this.homeCardFactory = delegate.homeCardFactory;
    }
    if (delegate.descriptorOverride !== undefined) {
      this.descriptorOverride = delegate.descriptorOverride;
    }
  }

  makeCell(): HTMLElement {
    return document.createElement("div");
  }

  bindCell(_$cell: HTMLElement, _context: BindCellContext): void {}
  onResize(_$cell: HTMLElement, _context: BindCellContext): void {}

  optionsInfo(row: SheetRow.Joined.Handle): Option<string | OptionsInfo> {
    return this.#delegate.optionsInfo(row);
  }

  onBindRow(handle: SheetDelegate, row: SheetRow.Joined.Handle): void {
    this.#delegate.onBindRow(handle, row);
  }

  onViewportColumnsChanged(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
    $legend: Option<$Legend>,
    viewportChangeSource: ScrollEngine.ViewportChangeSource,
  ): void {
    this.#delegate.onViewportColumnsChanged(
      row,
      columns,
      handle,
      $legend,
      viewportChangeSource,
    );
  }

  onRenderedColumnsWillChange(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
  ): void {
    this.#delegate.onRenderedColumnsWillChange(row, columns, handle);
  }

  staticChildren(
    row: SheetRow.Joined.Handle,
    handle: SheetDelegate,
  ): StaticChildSpec[] {
    return this.#delegate.staticChildren?.(row, handle) ?? [];
  }

  fixedChildrenForRow(
    row: SheetRow.Joined.Handle,
    context: FixedChildRowContext,
  ): Promise<FixedChildSpec[]> {
    return this.#delegate.fixedChildrenForRow?.(row, context) ??
      Promise.resolve([]);
  }
}

/** The plugins bound to one row, resolved through the sheet's plugin manager (`pluginManager.get(row)`). */
export class RowPluginHandle {
  readonly #manager: RenderPluginManager;
  constructor(
    manager: RenderPluginManager,
    readonly row: SheetRow.Joined.Handle,
  ) {
    this.#manager = manager;
  }

  plugin(): RenderPlugin.Resolved {
    const binding = this.#binding();
    return this.#manager.pluginForBinding(binding);
  }

  getIcon(): HTMLTemplateElement {
    const plugin = this.plugin();
    return plugin.iconForRow?.(this.row) ?? plugin.icon;
  }

  #binding(): RenderBinding {
    const variant = liveVariantOf(this.row);
    if (variant) {
      return RenderBinding.resolveChrome(variant, this.#manager.asLookup());
    }
    return this.#manager.legacyChromeBinding(this.row);
  }
}

// ── RenderPluginManager ──────────────────────────────────────────

/**
 * A registered plugin at its erased row view — what the manager holds, and
 * what the sheet binder binds cells through (`bindableLookup`). A plugin's
 * row view `V` appears both in what it takes (`bindCell`) and in what it
 * hands back (`rowClass`), so no concrete `RenderPlugin<…>` is a supertype of
 * every plugin; the binder pairs each plugin with the row its `rowClass`
 * names before it binds. Not on a published entry: a lookup hands out
 * `RenderPlugin.Resolved`, which has no row-view-typed member.
 */
export type BindablePlugin = RenderPlugin<HTMLElement, HTMLElement, unknown>;

/**
 * The binder's lookups over a manager, at the erased row view
 * (`bindableLookup(manager)`). Sheet-internal: not on a published entry.
 */
export interface BindableLookup {
  /** The plugin for `(type, role)`, or the manager's fallback. */
  plugin(type: string, role: Role): BindablePlugin;
  /** The plugin registered for `(type, role)` in that role; `null` when the lookup only reaches the fallback or another role. */
  registered(type: string, role: Role): Option<BindablePlugin>;
  /** The renderer for `type`; throws `RenderBinding.NotCellBindableError` for an alias plugin. */
  cellPlugin(type: string): BindablePlugin;
}

/** `RenderPluginManager`'s friend record (`Internals`): its constructor registers each instance. */
type RenderPluginManagerInternals = {
  readonly lookup: (manager: RenderPluginManager) => BindableLookup;
};
const renderPluginManagerInternals = new Internals<RenderPluginManager, RenderPluginManagerInternals>("RenderPluginManager");

/**
 * The binder's door to `manager`'s plugins at their erased row view: the one
 * place a plugin's `bindCell` / `onResize` / `rowClass` is reached. Throws for
 * a plugin manager that is not a `RenderPluginManager`.
 */
export function bindableLookup(manager: SheetDelegate.PluginManager): BindableLookup {
  if (!(manager instanceof RenderPluginManager)) {
    throw new Error("bindableLookup: the sheet's plugin manager is not a RenderPluginManager");
  }
  return renderPluginManagerInternals.of(manager).lookup(manager);
}

/**
 * Registry of render-only plugins. Looks up plugins by role-qualified
 * descriptor identity; row-type compatibility lives on explicit aliases.
 * No metadata methods — all metadata goes through the `RowTypeDescriptor`.
 *
 * A lookup hands out a `RenderPlugin.Resolved` (the plugin without its
 * row-view-typed members) and `plugins()` lists `RenderPlugin.Info`s: the
 * manager holds plugins of every row view and does not claim one it never
 * checked.
 */
export class RenderPluginManager {
  static readonly #internals: RenderPluginManagerInternals = {
    lookup: (manager) => ({
      plugin: (type, role) => manager.#pluginForKey(type, role),
      registered: (type, role) => manager.#maybePluginForRole(type, role),
      cellPlugin: (type) => manager.#cellPlugin(type),
    }),
  };

  readonly #fallback: BindablePlugin;
  /**
   * @param fallback the plugin a lookup answers when nothing is registered
   *   under the key (the unknown-row-type surface). Like a registered
   *   plugin it is taken at its own row view and held erased, so a renderer
   *   whose rows are not `SheetRow.Joined.Handle` can be the fallback.
   */
  constructor(fallback: RenderPlugin.AtOwnRowView) {
    renderPluginManagerInternals.register(this, RenderPluginManager.#internals);
    this.#fallback = fallback;
  }

  #_plugins = new Map<string, BindablePlugin>();
  #_receipt: RegistrationReceipt = RegistrationReceipt.EMPTY;

  /**
   * Register one plugin and RETURN WHAT IT CLAIMED.
   *
   * One `register` call writes the plugin under several keys — its canonical
   * `(id, role)` plus every alias `RenderPluginSpec.lookupIds` attaches — and
   * the receipt is that expansion as a value. It is the answer to "which keys
   * does the DOM answer?", which used to be knowable only from a second,
   * hand-maintained manifest of the same triples (`plugin-keys.ts`).
   *
   * Registration itself is unchanged: same keys, same last-write-wins map.
   */
  register<
    $C extends HTMLElement,
    $L extends HTMLElement,
    V = SheetRow.Joined.Handle,
  >(p: RenderPlugin<$C, $L, V>): RegistrationReceipt {
    const receipt = RegistrationReceipt.forSpec({
      id: p.id,
      role: p.role,
      ...(p.aliases !== undefined ? { aliases: p.aliases } : {}),
    });
    for (const key of receipt.keys) this.#_plugins.set(key, p);
    this.#_receipt = RegistrationReceipt.merge(this.#_receipt, receipt);
    return receipt;
  }

  /**
   * Chaining form of {@link register}, kept because every registration site
   * chains (`mgr.plugin(a).plugin(b)`) and the app builds its whole manager
   * that way.
   */
  plugin<
    $C extends HTMLElement,
    $L extends HTMLElement,
    V = SheetRow.Joined.Handle,
  >(p: RenderPlugin<$C, $L, V>): this {
    this.register(p);
    return this;
  }

  /**
   * The cumulative receipt across every registration on this manager — the
   * manager's actual answered surface, which the plugin-registration parity
   * test compares to the shared catalog.
   */
  get receipt(): RegistrationReceipt {
    return this.#_receipt;
  }

  /**
   * Look a plugin up AND say why, when the answer is the fallback.
   *
   * {@link pluginForKey} is the hot path and stays allocation-free: on a miss
   * it hands back `fallback` and says nothing, so "never registered" and
   * "registered under a different role" are indistinguishable at the call
   * site. This is its explaining twin — same `_plugins` reads through the same
   * keys, so the two always agree on the plugin, plus the named reason from
   * the receipt. Diagnostic surface only; nothing on the bind path calls it.
   */
  resolve(type: string, role: Role): RenderPluginManager.Resolution {
    const lookup = RegistrationReceipt.resolve(this.#_receipt, type, role);
    if (lookup.kind === "missing") {
      return { plugin: this.#fallback, lookup };
    }
    return {
      plugin: this.#_plugins.get(lookup.key) ?? this.#fallback,
      lookup,
    };
  }

  get(row: SheetRow.Joined.Handle): RowPluginHandle;
  get(
    type: string,
    role: Role,
  ): RenderPlugin.Resolved;
  get(
    ...args:
      | [row: SheetRow.Joined.Handle]
      | [type: string, role: Role]
  ): RowPluginHandle | RenderPlugin.Resolved {
    if (args.length === 1) return new RowPluginHandle(this, args[0]);
    const [type, role] = args;
    return this.#pluginForKey(type, role);
  }

  #pluginForKey(
    type: string,
    role: Role,
  ): BindablePlugin {
    const normalized = RenderPluginSpec.normalizeId(type);
    return this.#_plugins.get(RenderPluginSpec.mapKey(type, role)) ??
      this.#_plugins.get(RenderPluginSpec.mapKey(normalized, role)) ??
      this.#fallback;
  }

  pluginForBinding(
    binding: Pick<RenderBinding, "pluginId" | "pluginRole">,
  ): RenderPlugin.Resolved {
    return this.#pluginForKey(binding.pluginId, binding.pluginRole);
  }

  asLookup(): RenderPluginLookup {
    return {
      get: (id: string, role: Role): RenderPluginSpec => {
        const plugin = this.#pluginForKey(id, role);
        return {
          id: plugin.id,
          role: plugin.role,
          ...(plugin.aliases !== undefined ? { aliases: plugin.aliases } : {}),
        };
      },
    };
  }

  legacyChromeBinding(row: SheetRow.Joined.Handle): RenderBinding {
    const plugin = this.#legacyChromePlugin(row);
    return {
      surface: "chrome",
      requestedRowId: row.id,
      pluginRowId: row.id,
      sourceRowId: plugin.role === "source" ? row.id : null,
      pluginId: plugin.id,
      pluginRole: plugin.role,
      rowRole: plugin.role,
      contentType: row.descriptor.id,
      bindingKind: "row-descriptor",
      boundRowId: null,
      boundRendererId: null,
    };
  }

  #legacyChromePlugin(
    row: SheetRow.Joined.Handle,
  ): BindablePlugin {
    return this.#maybePluginForRole(row.descriptor.id, "layout") ??
      this.#maybePluginForRole(row.descriptor.id, "renderer") ??
      this.#maybePluginForRole(row.descriptor.id, "source") ??
      this.#fallback;
  }

  #maybePluginForRole(
    type: string,
    role: Role,
  ): Option<BindablePlugin> {
    const plugin = this.#pluginForKey(type, role);
    return plugin.role === role && plugin.id !== this.#fallback.id
      ? plugin
      : null;
  }

  /**
   * Legacy renderer-default lookup kept for old SheetPluginManager callers:
   * `role` `null` looks the renderer up.
   */
  getPlugin(
    type: string,
    role: Option<Role>,
  ): RenderPlugin.Resolved {
    return this.get(type, role ?? "renderer");
  }

  getCellPlugin(type: string): RenderPlugin.Resolved {
    return this.#cellPlugin(type);
  }

  #cellPlugin(type: string): BindablePlugin {
    const plugin = this.#pluginForKey(type, "renderer");
    if (plugin.role === "alias") {
      throw new RenderBinding.NotCellBindableError(
        `${type}: alias plugins are not cell-bindable in the sheet binder`,
      );
    }
    return plugin;
  }

  getLayoutPlugin(type: string): RenderPlugin.Layout<HTMLElement, HTMLElement> {
    const plugin = this.get(type, "layout");
    if (plugin.role !== "layout") {
      throw new Error(
        `${type}: expected layout plugin, got ${plugin.role}`,
      );
    }
    return plugin as RenderPlugin.Layout<HTMLElement, HTMLElement>;
  }

  getRendererPlugin(
    type: string,
  ): RenderPlugin.Renderer<HTMLElement, HTMLElement> {
    const plugin = this.get(type, "renderer");
    if (plugin.role !== "renderer") {
      throw new Error(
        `${type}: expected renderer plugin, got ${plugin.role}`,
      );
    }
    return plugin as RenderPlugin.Renderer<HTMLElement, HTMLElement>;
  }

  getSourcePlugin(type: string): RenderPlugin.SourceRow<HTMLElement, HTMLElement> {
    const plugin = this.get(type, "source");
    if (plugin.role !== "source") {
      throw new Error(
        `${type}: expected source plugin, got ${plugin.role}`,
      );
    }
    return plugin as RenderPlugin.SourceRow<HTMLElement, HTMLElement>;
  }

  /**
   * Collect descriptor overrides from all registered plugins.
   * Used at init time to register overrides on the SDK client.
   */
  descriptorOverrides(): Iterable<[string, RowTypeDescriptor.Override]> {
    const result: [string, RowTypeDescriptor.Override][] = [];
    const seen = new Set<BindablePlugin>();
    const seenKeys = new Set<string>();
    for (const plugin of this.#_plugins.values()) {
      if (seen.has(plugin)) continue;
      seen.add(plugin);
      if (plugin.descriptorOverride) {
        const role = plugin.role;
        const keys = RenderPluginSpec.lookupIds({
          id: plugin.id,
          role,
          ...(plugin.aliases !== undefined ? { aliases: plugin.aliases } : {}),
        });
        for (const key of keys) {
          if (seenKeys.has(key)) continue;
          seenKeys.add(key);
          result.push([key, plugin.descriptorOverride]);
        }
      }
    }
    return result;
  }

  /**
   * Every registered plugin, once each, as a frozen `RenderPlugin.Info` (its
   * id, role and aliases) — a listing; `get(id, role)` looks one up.
   */
  plugins(): Iterable<RenderPlugin.Info> {
    return [...new Set(this.#_plugins.values())].map((plugin) =>
      Object.freeze({
        id: plugin.id,
        role: plugin.role,
        aliases: plugin.aliases ?? null,
      })
    );
  }
}

/** Types that go with `RenderPluginManager`: `Resolution`. */
export namespace RenderPluginManager {
  /** A plugin plus the receipt lookup that explains how it was reached. */
  export interface Resolution {
    readonly plugin: RenderPlugin.Resolved;
    readonly lookup: RegistrationLookup;
  }
}

